import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

function authConfig(token, json = false) {
  const headers = { Authorization: `Bearer ${token}` };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function AdminTypes({ token }) {
  const [types, setTypes] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [ask, confirmDialog] = useConfirm();

  const load = async () => {
    const { data } = await axios.get("/api/admin/entry-types/", authConfig(token));
    setTypes(data);
    setError("");
  };

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
    // load is recreated each render and only needs to run when the token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openAdd = () => {
    setEditing(null);
    setTitle("");
    setNotice("");
    setError("");
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setTitle(item.title);
    setNotice("");
    setError("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
  };

  const save = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      if (editing) {
        await axios.put(
          `/api/admin/entry-types/${editing.id}/`,
          { title: title.trim() },
          authConfig(token, true)
        );
        setNotice("Type saved.");
      } else {
        await axios.post("/api/admin/entry-types/", { title: title.trim() }, authConfig(token, true));
        setNotice("Type added. Check it on an event to show it on the registration form.");
      }
      close();
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const remove = async (item) => {
    if (!(await ask(removePrompt(item.title)))) {
      return;
    }
    setNotice("");
    setError("");
    try {
      await axios.delete(`/api/admin/entry-types/${item.id}/`, authConfig(token));
      setNotice("Type removed.");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <>
      {confirmDialog}
      <h2>Types</h2>
      <p>
        These are the choices on the registration form. Display, Concours, and Street/Race start the list.
        Add a type here, then check it on the event that should offer it.
      </p>
      {notice && <Message variant="success">{notice}</Message>}
      {error && <Message variant="danger">{error}</Message>}
      <AdminAddButton onClick={openAdd}>Add a type</AdminAddButton>
      <AdminFormModal
        show={open}
        title={editing ? `Edit ${editing.title}` : "Add a type"}
        onHide={close}
      >
        <Form onSubmit={save}>
          <Form.Group className="mb-3" controlId="entryTypeTitle">
            <Form.Label>Type</Form.Label>
            <Form.Control value={title} onChange={(event) => setTitle(event.target.value)} required />
          </Form.Group>
          <AdminFormActions saveLabel={editing ? "Save type" : "Add type"} onCancel={close} />
        </Form>
      </AdminFormModal>
      {types.length === 0 ? (
        <Message variant="info" className="mt-4">No types yet.</Message>
      ) : (
        <div className="admin-records">
          {types.map((item) => (
            <article key={item.id} className="admin-record">
              <div className="admin-record-copy">
                <strong>{item.title}</strong>
              </div>
              <RecordActions
                name={item.title}
                onEdit={() => openEdit(item)}
                onRemove={() => remove(item)}
              />
            </article>
          ))}
        </div>
      )}
    </>
  );
}

export default AdminTypes;
